import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FormGrid, SelectField, TextAreaField, TextField } from "./Fields";

export function InterviewForm({ open, onOpenChange, defaults, onSaved }) {
  const { meta } = useAuth();
  const [form, setForm] = useState({});
  const [candidates, setCandidates] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      candidate_id: null, job_id: null, round_name: "Interview 1", interview_type: "Technical",
      interviewer_id: null, scheduled_at: new Date(Date.now() + 864e5).toISOString().slice(0, 16),
      duration_minutes: 45, meeting_link: "", notes: "", ...(defaults || {}),
    });
    api.get("/candidates", { params: { limit: 200 } })
      .then((r) => setCandidates(r.data.items))
      .catch(() => setCandidates([]));
  }, [open, defaults]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.candidate_id || !form.job_id) return toast.error("Select both a candidate and a job");
    setSaving(true);
    try {
      const { data } = await api.post("/interviews", {
        ...form, scheduled_at: new Date(form.scheduled_at).toISOString(),
      });
      toast.success("Interview scheduled — calendar invites sent");
      onOpenChange(false);      onSaved?.(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl border-slate-200 bg-white">
        <DialogHeader>
          <DialogTitle className="text-lg font-medium text-slate-900">Schedule an interview</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <FormGrid>
            <SelectField label="Candidate" value={form.candidate_id} onChange={set("candidate_id")} testid="interview-candidate-select"
              options={candidates.map((c) => ({ value: c.id, label: `${c.full_name}${c.current_designation ? ` · ${c.current_designation}` : ""}` }))} />
            <SelectField label="Job" value={form.job_id} onChange={set("job_id")} testid="interview-job-select"
              options={(meta?.jobs || []).map((j) => ({ value: j.id, label: `${j.job_code} · ${j.title}` }))} />
            <SelectField label="Round" value={form.round_name} onChange={set("round_name")}
              options={["Interview 1", "Interview 2", "Final Interview", "Screening Call"]} testid="interview-round-select" />
            <SelectField label="Interview type" value={form.interview_type} onChange={set("interview_type")}
              options={meta?.enums?.interview_types || []} testid="interview-type-select" />
            <SelectField label="Interviewer" value={form.interviewer_id} onChange={set("interviewer_id")}
              options={(meta?.users || []).map((u) => ({ value: u.id, label: u.name }))} testid="interview-interviewer-select" />
            <TextField label="Duration (mins)" type="number" value={form.duration_minutes} onChange={set("duration_minutes")} />
            <TextField label="Date &amp; time" type="datetime-local" value={form.scheduled_at} onChange={set("scheduled_at")} testid="interview-datetime-input" />
            <TextField label="Meeting link" value={form.meeting_link} onChange={set("meeting_link")} placeholder="https://meet…" />
          </FormGrid>
          <TextAreaField label="Notes for the panel" value={form.notes} onChange={set("notes")} rows={3} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-500">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="interview-submit-button" className="gap-2 rounded-full bg-blue-600 px-5 hover:bg-blue-500">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TaskForm({ open, onOpenChange, defaults, onSaved }) {
  const { meta, user } = useAuth();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      title: "", task_type: "Call candidate", description: "", assignee_id: user?.id,
      due_date: new Date().toISOString().slice(0, 10), priority: "Medium",
      candidate_id: null, job_id: null, client_id: null, ...(defaults || {}),
    });
  }, [open, defaults, user]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Task title is required");
    setSaving(true);
    try {
      const { data } = await api.post("/tasks", form);
      toast.success("Task created");
      onOpenChange(false);
      onSaved?.(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl border-slate-200 bg-white">
        <DialogHeader>
          <DialogTitle className="text-lg font-medium text-slate-900">Create a task</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <TextField label="Title" value={form.title} onChange={set("title")} required testid="task-title-input" placeholder="Follow up with client on shortlist" />
          <FormGrid>
            <SelectField label="Task type" value={form.task_type} onChange={set("task_type")} options={meta?.enums?.task_types || []} testid="task-type-select" />
            <SelectField label="Priority" value={form.priority} onChange={set("priority")} options={meta?.enums?.priorities || []} />
            <SelectField label="Assignee" value={form.assignee_id} onChange={set("assignee_id")} testid="task-assignee-select"
              options={(meta?.users || []).map((u) => ({ value: u.id, label: u.name }))} />
            <TextField label="Due date" type="date" value={form.due_date} onChange={set("due_date")} testid="task-due-input" />
            <SelectField label="Related job" value={form.job_id} onChange={set("job_id")}
              options={(meta?.jobs || []).map((j) => ({ value: j.id, label: `${j.job_code} · ${j.title}` }))} />
            <SelectField label="Related client" value={form.client_id} onChange={set("client_id")}
              options={(meta?.clients || []).map((c) => ({ value: c.id, label: c.name }))} />
          </FormGrid>
          <TextAreaField label="Description" value={form.description} onChange={set("description")} rows={3} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-500">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="task-submit-button" className="gap-2 rounded-full bg-blue-600 px-5 hover:bg-blue-500">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Create task
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FeedbackForm({ open, onOpenChange, interview, onSaved }) {
  const { meta } = useAuth();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open)
      setForm({
        technical_skills: 4, communication: 4, problem_solving: 4, cultural_fit: 4,
        overall_rating: 4, strengths: "", weaknesses: "", recommendation: "Hire", comments: "",
      });
  }, [open]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/interviews/${interview.id}/feedback`, form);
      toast.success("Feedback submitted");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const RATINGS = ["1", "2", "3", "4", "5"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl border-slate-200 bg-white">
        <DialogHeader>
          <DialogTitle className="text-lg font-medium text-slate-900">
            Interview feedback · {interview?.candidate_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <FormGrid>
            {["technical_skills", "communication", "problem_solving", "cultural_fit", "overall_rating"].map((k) => (
              <SelectField
                key={k}
                label={k.replace(/_/g, " ")}
                value={String(form[k] ?? 4)}
                onChange={(v) => set(k)(Number(v))}
                options={RATINGS}
                testid={`feedback-${k}`}
              />
            ))}
            <SelectField label="Recommendation" value={form.recommendation} onChange={set("recommendation")}
              options={meta?.enums?.recommendations || []} testid="feedback-recommendation" />
          </FormGrid>
          <TextAreaField label="Strengths" value={form.strengths} onChange={set("strengths")} rows={2} testid="feedback-strengths" />
          <TextAreaField label="Areas of concern" value={form.weaknesses} onChange={set("weaknesses")} rows={2} />
          <TextAreaField label="Additional comments" value={form.comments} onChange={set("comments")} rows={2} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-500">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="feedback-submit-button" className="gap-2 rounded-full bg-blue-600 px-5 hover:bg-blue-500">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Submit feedback
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function OfferForm({ open, onOpenChange, defaults, onSaved }) {
  const { meta } = useAuth();
  const [form, setForm] = useState({});
  const [candidates, setCandidates] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      candidate_id: null, job_id: null, offered_ctc: null, joining_bonus: null, location: "",
      joining_date: new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10),
      status: "Offer Draft", notes: "", ...(defaults || {}),
    });
    api.get("/candidates", { params: { limit: 200 } })
      .then((r) => setCandidates(r.data.items))
      .catch(() => {});
  }, [open, defaults]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.candidate_id || !form.job_id) return toast.error("Select both a candidate and a job");
    if (!form.offered_ctc) return toast.error("Offered CTC is required");
    setSaving(true);
    try {
      const { data } = await api.post("/offers", form);
      toast.success("Offer created");
      onOpenChange(false);
      onSaved?.(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl border-slate-200 bg-white">
        <DialogHeader>
          <DialogTitle className="text-lg font-medium text-slate-900">Create an offer</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <FormGrid>
            <SelectField label="Candidate" value={form.candidate_id} onChange={set("candidate_id")} testid="offer-candidate-select"
              options={candidates.map((c) => ({ value: c.id, label: c.full_name }))} />
            <SelectField label="Job" value={form.job_id} onChange={set("job_id")} testid="offer-job-select"
              options={(meta?.jobs || []).map((j) => ({ value: j.id, label: `${j.job_code} · ${j.title}` }))} />
            <TextField label="Offered CTC (LPA)" type="number" value={form.offered_ctc} onChange={set("offered_ctc")} testid="offer-ctc-input" />
            <TextField label="Joining bonus (₹)" type="number" value={form.joining_bonus} onChange={set("joining_bonus")} />
            <TextField label="Location" value={form.location} onChange={set("location")} />
            <TextField label="Joining date" type="date" value={form.joining_date} onChange={set("joining_date")} testid="offer-joining-date" />
            <SelectField label="Offer status" value={form.status} onChange={set("status")} options={meta?.enums?.offer_statuses || []} />
          </FormGrid>
          <TextAreaField label="Notes" value={form.notes} onChange={set("notes")} rows={2} />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-500">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="offer-submit-button" className="gap-2 rounded-full bg-blue-600 px-5 hover:bg-blue-500">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Create offer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
